
import { h, Fragment, useRef } from 'jsx-dom'


// helpers

const noop = () => {}
const json = id => document.querySelector(id) && JSON.parse(document.querySelector(id).textContent) || null

const hash = str => {
	// very simple (not secure!) hash to generate input ids
	// from: https://gist.github.com/jlevy/c246006675becc446360a798e2b2d781
	let hash = 0
	for (let i = 0; i < str.length; i++) {
		const char = str.charCodeAt(i)
		hash = (hash << 5) - hash + char
		hash &= hash // Convert to 32bit integer
	}
	return new Uint32Array([hash])[0].toString(36)
}

const nodeType = (props) => {
	const types = {
		'question' : Question,
		'answer' : Answer,
		'answer_and_form' : Answer,
	}
	return types[props?.layout] || Fragment
}


// components

const App = ({ rerender, questions, selected }) => {

	const firstQuestionId = Object.keys(questions)[0]
	if (!selected.length) selected = [firstQuestionId]

	const goTo = (newId, fromIndex) => {	
		const nextSelected = selected.slice(0, fromIndex + 1)
		const currentQuestion = questions[ nextSelected.slice(-1)[0] ]
		const selectedAnswer = currentQuestion?.answers.filter(a => a.answer_id === newId)[0] || null
		const question = questions[newId]
		if (!selectedAnswer) throw new Error(`No answer with id ${newId} found on current question ${currentQuestion.id}`)
		if (!question) throw new Error(`No question with id ${newId} found`)
		nextSelected.push(newId)
		rerender(questions, nextSelected)
	}

	const selectedQuestions = selected.map(id => questions[id])

	return <>
		<section class="pathfinder margin-y page-width">
			{selectedQuestions.map((question, index) => {
				const next = selectedQuestions[index + 1]
				const Node = nodeType(question)
				return <Node {...question} index={index} onSelectAnswer={goTo} selectedAnswerId={next?.id} />
			})}
		</section>
	</>
}


const QuestionOption = ({ text, selected, onClick }) => {
	const classes = [
		'pathfinder__answer',
		'answer__padding',
		selected && 'is__selected'
	]
	return <button class={classes} onClick={onClick}>{text}</button>
}


const Question = ({ index, image, content, question, answers, onSelectAnswer = noop, selectedAnswerId, ...props }) => {
	
	const imageSrc = image?.sizes?.medium_large
	const imageWidth = image?.sizes?.['medium_large-width'] || null
	const imageHeight = image?.sizes?.['medium_large-height'] || null
	const string = 'background-image:url(' + imageSrc + ')'

	return <>
		{content && <div class="pathfinder__results grid@medium block-padding margin-y-2">
			{imageSrc && <>
				<div class="col-5@medium pathfinder__results__image" style={string}></div>
				<div class="pathfinder__results__content col-6@medium" innerHTML={content} /> 
			</>}
			{!imageSrc && <>
				<div class="col-12 pathfinder__results__content text-center" innerHTML={content} /> 
			</>}
		</div>}

		{question && <div class="pathfinder__question margin-y-2 text-white bg--petrol block-padding-large">
			<h2 class="heading-4 text--center heading--wrapper" innerHTML={question} />
		</div>}

		<div class="pathfinder__answer__wrap margin-y-2 text-main--large">
			{answers.map(item => {	
				const {answer, answer_id}  = item
				return <QuestionOption 
					text={answer} 
					selected={selectedAnswerId === answer_id} 
					onClick={() => onSelectAnswer(answer_id, index)} 
				/>
			})}
		</div>
	</>
}


const Answer = ({ index, image, content, title, link, ...props }) => {

	const imageSrc = image?.sizes?.medium_large
	const imageWidth = image?.sizes?.['medium_large-width'] || null
	const imageHeight = image?.sizes?.['medium_large-height'] || null
	const string = 'background-image:url(' + imageSrc + ')'
	
	const mainContent = <>
		<h4 class="heading-4 text-teal heading--bold padding-top-1@small">{title}</h4>
		<div innerHTML={content} />
		{link && <a class="button--fill button--small@medium heading--bold margin-top-2 bg--brand" target={link.target} href={link.url}>{link.title}</a>}
	</>

	return <>
		<div class="pathfinder__results grid@medium block-padding margin-y-2">
			{imageSrc && <>
				<div class="grid">
					<div class="col-5@medium pathfinder__results__image" style={string}></div>
					<div class="pathfinder__results__content col-6@medium pathfinder__hasimage">
						{mainContent}
						<FormContent {...props} />
					</div>
				</div>
			</>}
			{!imageSrc && <>
				<div class="pathfinder__results__content col-12">
					{mainContent}
					<FormContent {...props} hasImage={false} />
				</div> 
			</>}			
		</div>
	</>
}


const FormContent = ({ hasImage = true, ...props }) => {

	const { 
		form_heading : formHeading, 
		form_subheading : formSubheading, 
		form_fields : formFields, 
		form_link : formLink,
		form_information : formInformation,
	} = props

	const link = useRef()
	const checklist = useRef()
	const initialLinkStyle = { cursor: 'not-allowed', opacity: 0.2, background: '#0D535F' }

	return <>
		{formHeading && <p>
			<strong class="text-teal">{formHeading}</strong><br />
			{formSubheading}
		</p>}

		{formFields && 
			<ul class={[ 'pathfinder__checklist', !hasImage && 'pathfinder__checklist--center' ]} ref={checklist}>
				{formFields.map((field, i) => {
					const id = hash(i + field.option)
					return <li class="pathfinder__checklist__item">
						<input type="checkbox" id={id} name={id} value={true} class="ref-input" onClick={clickHandler} />
						<span class="checkmark"></span>
						<label for={id} innerHTML={field.option}></label>
					</li>
				})}
			</ul>
		}

		{formInformation && 
			<><div innerHTML={formInformation} /></>
		}

		{formLink && 
			<a 
				ref={link} 
				href={formLink.url} 
				target={formLink.target}
				class="button--fill button--small@medium heading--bold margin-top-2 bg--brand"
				style={initialLinkStyle}>
					{formLink.title}
			</a>
		}
	</>

	function clickHandler() {
		allChecked() ? (link.current.style = '') : Object.assign(link.current.style, initialLinkStyle)
	}

	function allChecked() {
		const inputs = [...checklist.current.querySelectorAll('.ref-input')]
		for (const el of inputs) {
			if (!el.checked) return false
		}
		return true
	}
}


export default () => {

	const container = document.querySelector('#pathfinder')
	const questions = json('#pathfinder-json')
	if (!container || !questions) return

	function render(questions, selected = []) {
		const el = <App rerender={render} questions={questions} selected={selected} />
		container.innerHTML = ''
		container.appendChild(el)
	}

	render(questions)	
}